import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface RouteBoundaryProps {
  scope: string;
  children: ReactNode;
  /** Optional custom fallback. Defaults to the shared route-level card. */
  fallback?: ReactNode;
  /** When true, uses a compact inline fallback (for widgets, not full pages). */
  compact?: boolean;
}

/**
 * Route- or feature-level error boundary.
 *
 * - Re-mounts on route change so a crash on one page doesn't stick to the next
 *   (keyed by pathname).
 * - Shows a human-readable recovery card with retry + navigation, never a
 *   stack trace.
 * - Reports every failure through the observability abstraction.
 */
export function RouteBoundary({ scope, children, fallback, compact }: RouteBoundaryProps) {
  const location = useLocation();
  const defaultFallback = compact ? (
    <div className="p-6 border border-border rounded-lg bg-muted/40 text-sm text-muted-foreground">
      This section couldn't load. Refresh the page to try again.
    </div>
  ) : (
    <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 p-8 text-center">
      <h2 className="text-2xl font-semibold text-foreground">This page couldn't load</h2>
      <p className="text-muted-foreground max-w-md">
        Something went wrong on our end and our team has been notified. You can retry, or continue browsing.
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
        <Button asChild><Link to="/">Go home</Link></Button>
        <Button variant="ghost" asChild><Link to="/contact">Contact us</Link></Button>
      </div>
    </div>
  );

  return (
    <ErrorBoundary key={location.pathname} scope={scope} fallback={fallback ?? defaultFallback}>
      {children}
    </ErrorBoundary>
  );
}

export default RouteBoundary;